import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiLockKeyBold, PiLockKeyOpenBold } from 'react-icons/pi';
import { Button, Skeleton } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import type { MonthClosingInfo } from './types';

type MonthClosingRow = MonthClosingInfo & { snapshot_sellers: number };

type MonthClosingsResponse = {
    data: MonthClosingRow[];
    next_open_competence: string;
    can_close: boolean;
    can_reopen: boolean;
};

const QUERY_KEY = ['commissioning', 'month-closings'];

function fmtMonth(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

function fmtDateTime(iso?: string | null) {
    return iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}

function apiMessage(err: unknown): string | null {
    const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
    return data?.message ?? null;
}

export function MonthClosedBadge({ closing }: { closing?: MonthClosingInfo | null }) {
    const { t } = useI18n();
    if (!closing) return null;

    return (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200">
            <PiLockKeyBold className="mt-0.5 size-4 shrink-0" aria-hidden />
            <div>
                <p className="font-bold">{t('v2.commissioning.month_closing.closed_badge')}</p>
                <p className="mt-0.5">
                    {t('v2.commissioning.month_closing.closed_hint', {
                        date: fmtDateTime(closing.closed_at),
                        name: closing.closed_by ?? '—',
                    })}
                </p>
            </div>
        </div>
    );
}

export default function MonthClosingCard() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [closeOpen, setCloseOpen] = useState(false);
    const [reopenTarget, setReopenTarget] = useState<string | null>(null);
    const [notes, setNotes] = useState('');
    const [reason, setReason] = useState('');
    const [competence, setCompetence] = useState('');

    const query = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => api.get('/commissioning/month-closings').then((r) => r.data as MonthClosingsResponse),
    });

    const refresh = () => {
        void qc.invalidateQueries({ queryKey: QUERY_KEY });
        void qc.invalidateQueries({ queryKey: ['commissioning', 'seller-month-history'] });
    };

    const close = useMutation({
        mutationFn: () => api.post('/commissioning/month-closings', { competence, notes: notes.trim() || null }),
        onSuccess: (res) => {
            toast.success(res.data?.message ?? t('v2.commissioning.month_closing.closed', { month: competence }));
            setCloseOpen(false);
            setNotes('');
            refresh();
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.commissioning.month_closing.already_closed')),
    });

    const reopen = useMutation({
        mutationFn: (month: string) => api.delete(`/commissioning/month-closings/${month}`, { data: { reason: reason.trim() } }),
        onSuccess: (res, month) => {
            toast.success(res.data?.message ?? t('v2.commissioning.month_closing.reopened', { month }));
            setReopenTarget(null);
            setReason('');
            refresh();
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.commissioning.month_closing.not_closed')),
    });

    if (query.isLoading) {
        return <Skeleton className="h-28 w-full rounded-xl" />;
    }

    const data = query.data;
    if (!data) return null;

    const latest = data.data.slice(0, 6);

    return (
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span className="flex size-10 items-center justify-center rounded-full bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-200">
                        <PiLockKeyBold className="size-5" aria-hidden />
                    </span>
                    <div>
                        <p className="text-sm font-bold text-gray-900 dark:text-zinc-50">{t('v2.commissioning.month_closing.title')}</p>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">{t('v2.commissioning.month_closing.subtitle')}</p>
                    </div>
                </div>
                {data.can_close && (
                    <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                            setCompetence(data.next_open_competence);
                            setCloseOpen(true);
                        }}
                    >
                        {t('v2.commissioning.month_closing.close_button')} · {fmtMonth(data.next_open_competence)}
                    </Button>
                )}
            </div>

            {latest.length === 0 ? (
                <p className="mt-3 text-xs text-gray-500">{t('v2.commissioning.month_closing.empty')}</p>
            ) : (
                <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                        <thead className="text-[10px] uppercase tracking-wide text-gray-500">
                            <tr>
                                <th className="py-1 pr-3">{t('v2.commissioning.month_closing.month')}</th>
                                <th className="py-1 pr-3">{t('v2.commissioning.month_closing.closed_at')}</th>
                                <th className="py-1 pr-3">{t('v2.commissioning.month_closing.closed_by')}</th>
                                <th className="py-1 pr-3">{t('v2.commissioning.month_closing.sellers')}</th>
                                <th className="py-1" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                            {latest.map((row) => (
                                <tr key={row.competence}>
                                    <td className="py-1.5 pr-3 font-semibold capitalize">{fmtMonth(row.competence)}</td>
                                    <td className="py-1.5 pr-3">{fmtDateTime(row.closed_at)}</td>
                                    <td className="py-1.5 pr-3">{row.closed_by ?? '—'}</td>
                                    <td className="py-1.5 pr-3">
                                        {row.snapshot_status === 'done' && row.snapshot_sellers}
                                        {row.snapshot_status === 'pending' && (
                                            <span className="text-amber-600">{t('v2.commissioning.month_closing.snapshot_pending')}</span>
                                        )}
                                        {row.snapshot_status === 'failed' && (
                                            <span className="text-rose-600">{t('v2.commissioning.month_closing.snapshot_failed')}</span>
                                        )}
                                    </td>
                                    <td className="py-1.5 text-right">
                                        {data.can_reopen && (
                                            <Button type="button" size="sm" variant="outline" onClick={() => setReopenTarget(row.competence)}>
                                                <PiLockKeyOpenBold className="mr-1 size-3.5" aria-hidden />
                                                {t('v2.commissioning.month_closing.reopen_button')}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <V2Modal
                open={closeOpen}
                onClose={() => setCloseOpen(false)}
                title={t('v2.commissioning.month_closing.close_button')}
                description={t('v2.commissioning.month_closing.close_confirm', { month: fmtMonth(competence) })}
                size="md"
                footer={(
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setCloseOpen(false)}>
                            {t('v2.commissioning.director_approval.close')}
                        </Button>
                        <Button type="button" isLoading={close.isPending} onClick={() => close.mutate()}>
                            {t('v2.commissioning.month_closing.close_button')}
                        </Button>
                    </div>
                )}
            >
                <div className="space-y-3">
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.commissioning.month_closing.month')}
                        <input
                            type="month"
                            value={competence}
                            onChange={(e) => setCompetence(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                    </label>
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.commissioning.month_closing.notes')}
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                    </label>
                </div>
            </V2Modal>

            <V2Modal
                open={reopenTarget != null}
                onClose={() => setReopenTarget(null)}
                title={t('v2.commissioning.month_closing.reopen_button')}
                description={reopenTarget ? fmtMonth(reopenTarget) : undefined}
                size="md"
                footer={(
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setReopenTarget(null)}>
                            {t('v2.commissioning.director_approval.close')}
                        </Button>
                        <Button
                            type="button"
                            isLoading={reopen.isPending}
                            disabled={reason.trim().length < 3}
                            onClick={() => reopenTarget && reopen.mutate(reopenTarget)}
                        >
                            {t('v2.commissioning.month_closing.reopen_button')}
                        </Button>
                    </div>
                )}
            >
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.commissioning.month_closing.reopen_reason')}
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                    />
                </label>
            </V2Modal>
        </section>
    );
}
